const DROP_HMR_TYPES = {
  removedPage: true,
  reloadPage: true,
  addedPage: true,
  middlewareChanges: true,
  serverOnlyChanges: true,
  clientChanges: true,
  serverComponentChanges: true,
};

function isLocalHost() {
  const host = String(window.location.hostname || "");
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
}

function isHmrSocketUrl(url) {
  const value = String(url || "");
  return (
    value.includes("/_next/") ||
    value.includes("/_next/hmr") ||
    value.includes("webpack-hmr") ||
    value.includes("turbopack")
  );
}

function filterHmrMessage(event, lastSessionRef) {
  try {
    const msg = JSON.parse(event.data);
    if (!msg || !msg.type) return event;
    if (DROP_HMR_TYPES[msg.type]) return null;

    if (msg.type === "turbopack-connected" && msg.data?.sessionId != null) {
      if (
        lastSessionRef.id != null &&
        lastSessionRef.id !== msg.data.sessionId
      ) {
        return new MessageEvent("message", {
          data: JSON.stringify({
            ...msg,
            data: { ...msg.data, sessionId: lastSessionRef.id },
          }),
        });
      }
      lastSessionRef.id = msg.data.sessionId;
    }
  } catch {
    // binary / non-JSON HMR frames
  }
  return event;
}

function createDummyHmrSocket() {
  const socket = {
    readyState: 1,
    OPEN: 1,
    CONNECTING: 0,
    CLOSING: 2,
    CLOSED: 3,
    bufferedAmount: 0,
    binaryType: "blob",
    url: "",
    onopen: null,
    onclose: null,
    onerror: null,
    onmessage: null,
    send() {},
    close() {},
    removeEventListener() {},
    dispatchEvent() {
      return true;
    },
    addEventListener(type, fn) {
      if (type === "open" && typeof fn === "function") {
        window.setTimeout(() => fn.call(socket, { type: "open", target: socket }), 0);
      }
    },
  };
  window.setTimeout(() => {
    if (typeof socket.onopen === "function") {
      socket.onopen({ type: "open", target: socket });
    }
  }, 0);
  return socket;
}

function patchWebSocket() {
  const NativeWS = window.WebSocket;
  if (!NativeWS || NativeWS.__nhWrapped) return;

  const lastSessionRef = { id: null };

  function WrappedWebSocket(url, protocols) {
    if (isHmrSocketUrl(url) && !isLocalHost()) {
      return createDummyHmrSocket();
    }

    const ws =
      protocols !== undefined
        ? new NativeWS(url, protocols)
        : new NativeWS(url);

    if (!isHmrSocketUrl(url)) return ws;

    const nativeAdd = ws.addEventListener.bind(ws);
    let userHandler = null;

    const deliver = (event) => {
      const next = filterHmrMessage(event, lastSessionRef);
      if (!next || typeof userHandler !== "function") return;
      userHandler.call(ws, next);
    };

    Object.defineProperty(ws, "onmessage", {
      configurable: true,
      enumerable: true,
      get() {
        return userHandler;
      },
      set(fn) {
        userHandler = fn;
        if (!ws.__nhMsgBound) {
          ws.__nhMsgBound = (event) => deliver(event);
          nativeAdd("message", ws.__nhMsgBound);
        }
      },
    });

    ws.addEventListener = function (type, listener, options) {
      if (type === "message" && typeof listener === "function") {
        return nativeAdd(
          type,
          (event) => {
            const next = filterHmrMessage(event, lastSessionRef);
            if (!next) return;
            return listener.call(this, next);
          },
          options
        );
      }
      return nativeAdd(type, listener, options);
    };

    return ws;
  }

  WrappedWebSocket.prototype = NativeWS.prototype;
  WrappedWebSocket.CONNECTING = NativeWS.CONNECTING;
  WrappedWebSocket.OPEN = NativeWS.OPEN;
  WrappedWebSocket.CLOSING = NativeWS.CLOSING;
  WrappedWebSocket.CLOSED = NativeWS.CLOSED;
  WrappedWebSocket.__nhWrapped = true;
  window.WebSocket = WrappedWebSocket;
}

function stripRefreshMeta() {
  document
    .querySelectorAll('meta[http-equiv="refresh" i]')
    .forEach((el) => el.remove());
}

function patchReloadProperty(obj, key) {
  try {
    const desc = Object.getOwnPropertyDescriptor(obj, key);
    const native = desc && typeof desc.value === "function" ? desc.value : obj[key];
    Object.defineProperty(obj, key, {
      configurable: true,
      writable: true,
      value() {
        if (window.__NH_ALLOW_RELOAD && typeof native === "function") {
          return native.apply(this, arguments);
        }
      },
    });
  } catch {
    try {
      obj[key] = function () {
        if (window.__NH_ALLOW_RELOAD) return;
      };
    } catch {
      // ignore
    }
  }
}

function patchLocationReload() {
  const isSameDocument = (url) => {
    try {
      const next = new URL(String(url), window.location.href);
      return (
        next.origin === window.location.origin &&
        next.pathname === window.location.pathname &&
        next.search === window.location.search
      );
    } catch {
      return false;
    }
  };

  if (window.Location?.prototype) {
    patchReloadProperty(window.Location.prototype, "reload");
  }
  patchReloadProperty(window.location, "reload");

  try {
    const replace = window.location.replace.bind(window.location);
    window.location.replace = function guardedReplace(url) {
      if (isSameDocument(url)) return;
      return replace(url);
    };
  } catch {
    // ignore
  }

  try {
    const assign = window.location.assign.bind(window.location);
    window.location.assign = function guardedAssign(url) {
      if (isSameDocument(url)) return;
      return assign(url);
    };
  } catch {
    // ignore
  }
}

/**
 * Stops silent full-page reloads from Next.js HMR (idle page dispose),
 * GTM/CMS meta refresh, and same-URL replace.
 *
 * Live/preprod is still served as `next dev`. After ~60s idle, Azure ARR
 * drops the HMR socket and Next calls native location.reload(). That method
 * is read-only on `window.location`, so we patch Location.prototype and
 * replace remote HMR sockets with a dummy that never disconnects.
 */
export function installReloadGuard() {
  if (typeof window === "undefined") return;

  patchLocationReload();
  patchWebSocket();
  stripRefreshMeta();
  window.__nhReloadGuard = true;

  try {
    const observer = new MutationObserver(stripRefreshMeta);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  } catch {
    // ignore
  }
}
